import { AuthCompany, AuthUser, CompanySettings, CustomCompanySettings, User } from 'src/models';

export class Login {
  static readonly type = '[Auth] Login';
  constructor(public email: string, public password: string, public rememberMe: boolean, public totpCode?: string) { }
}

export class LoginAs {
  static readonly type = '[Auth] Login As';
  constructor(public user: AuthUser, public token: string, public cookieAuth: boolean) { }
}

export class UpdateCurrentUser {
  static readonly type = '[Auth] Update Current User';

  constructor(public hideLoading: boolean = false) { }
}

export class Logout {
  static readonly type = '[Auth] Logout';
  constructor(public forget: boolean, public errorMessage?: string) { }
}

export class BeforeLogout {
  static readonly type = '[Auth] Before Logout';
}

export class AfterLogout {
  static readonly type = '[Auth] After Logout';
}

export class ForgotPassword {
  static readonly type = '[Auth] Forgot Password';
  constructor(public email: string) { }
}

export class SelectCompany {
  static readonly type = '[Auth] Select Company';
  constructor(public company: AuthCompany) { }
}

export class SyncCompanyFromPeerTab {
  static readonly type = '[Auth] Sync Company From Peer Tab';
  constructor(public company: AuthCompany) { }
}

export interface CancellationFlowData {
  currentStep?: number;
  selectedReason?: string;
  selectedSubReason?: string;
  finalThoughts?: string;
  lastUpdated?: string;
  contactSupportRequested?: boolean;
  /** True if user accepted the session discount; false if they explicitly skipped it */
  retentionDiscountChoice?: boolean;
  /** Increment when cancellation step model changes; used for persisted step migration */
  flowSchemaVersion?: number;
}

export interface ExtCustomCompanySettings extends CustomCompanySettings {
  trackedAccountCreated?: boolean;
  showSpecialOffer?: boolean;
  tdAnonymousId?: string;
  hasMandatoryCall?: boolean;
  trackedSteps?: {
    [key: string]: boolean;
  };
  cancellationFlow?: CancellationFlowData;
}

export class UserSettingsChanged {
  static readonly type = '[Auth] User Settings Changed';
  constructor(
    public changes: Partial<CompanySettings>,
    public custom?: ExtCustomCompanySettings,
  ) { }
}

/** Shallow `companySettings` fields merged by `CompanySettingsChanged` (excludes `custom`). */
type CompanySettingsActionSettingKeys =
  | 'name'
  | 'blurScreenshots'
  | 'webAndAppTracking'
  | 'enableLocationForSsid'
  | 'screenshots'
  | 'videos'
  | 'workCheckInterval'
  | 'canEditTime'
  | 'workScheduleFeature'
  | 'trackConnectivity'
  | 'aiConnectionsTracking'
  | 'enableAiConnections';

export class CompanySettingsChanged {
  static readonly type = '[Auth] Company Settings Changed';
  constructor(
    public rootChanges: Partial<Pick<AuthCompany, 'name' | 'timezone' | 'companyOwnerEmail' | 'pricingPlan' | 'userCount' | 'hasManagedApprovals'>>,
    public settingChanges: Partial<Pick<CompanySettings, CompanySettingsActionSettingKeys>>,
    public custom: ExtCustomCompanySettings,
  ) { }
}

export class InvalidateUser {
  static readonly type = '[Auth] Has Invalid Token';

  constructor(public errorMessage: string | null = 'login.tokenExpired') { }
}

export class TokenLogin {
  static readonly type = '[Auth] Token Login';
  constructor(public token: string, public company: string, public adminLogin?: boolean, public authMode?: 'v1' | 'v2') { }
}

export class ActivateCookieAuth {
  static readonly type = '[Auth] Activate Cookie Auth';
  constructor(public company?: string, public adminLogin?: boolean) { }
}

export class SetImpersonatePlan {
  static readonly type = '[Auth] Set Impersonate Plan';
}

export type StatusMode = 'offline' | 'computer' | 'manual' | 'break' | 'paidBreak' | 'unpaidBreak' | 'paidLeave' | 'unpaidLeave' | 'meeting';

export interface StatusPayload {
  callerId: string;
  companyId: string;
  deviceId: string;
  id: string;
  mode: StatusMode;
  onlyProjectIds: string[];
  paging: {
    getTotal: boolean;
    limit: number;
    skip: number;
  };
  remoteIp: string;
  userId: string;
  projectId?: string;
  taskId?: string;
  projectName?: string;
  taskName?: string;
}

export class StatusChanged {
  static readonly type = '[Auth] Status Changed';
  constructor(public data: StatusPayload) { }
}

export class EditProfile {
  static readonly type = '[ManageUsers] Edit Profile';
  constructor(public properties: Partial<User> & { password?: string, twoFactorAuth?: boolean }) { }
}

export class Register {
  static readonly type = '[Auth] Register';
  constructor(public name: string, public email: string, public password: string) { }
}

export class CollapseOnboardingChecklist {
  static readonly type = '[Dashboard] Collapse Checklist';
}

export class SaveCancellationFlowData {
  static readonly type = '[Auth] Save Cancellation Flow Data';
  constructor(public cancellationData: CancellationFlowData) { }
}
